import React, { useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import {
  CustomerDetails,
  Demographics,
  WorkExperience,
  SocialMedia,
  Documents,
} from "./";
import { State } from "../../../core/store/interfaces";
import { Customer } from "../../../core/api/models";
import Accordion from "../../../components/Accordion";
import "../../../styles/details.scss";
import api from "../../../api";
import {
  DateFormats,
  formatDate,
  useLead,
  useLog,
  capitalizeFirstLetter,
  AppPermissions,
  useHasPermission,
  StageTransition,
  getInvalidCustomerFieldsEnquiry,
  LeadQuality,
  getInvalidFields,
  Company,
  getMinutes
} from "../../../core";
import LinkedLeads from "./linked-leads/LinkedLeads";
import AssignUsers from "./assign-users/AssignUsers";
import Actions from "./actions/Actions";

import LeadEdit from "./LeadEdit";
import { LeadActivities } from "./activities/lead-activities";
import { useLeadCalendarContext } from "../../../core/hooks/useLeadCalendarContext";
import { EditEventCalendarWraper } from "../../../core/components/calendar/edit-event-calendar-wraper";
import { ActivityModel } from "./activities/add-activity";
import Showroom from "./showroom/Showroom";
import { useAppSettings } from "../../../core/hooks/useAppSettings";
import InvalidFieldsForEnquiryBanner from "./InvalidFieldsForEnquiryBanner";
import { useMasterData } from "../../../core/hooks/useMasterData";
import CustomerEdit from "../../customer/components/customer-stage-edit/CustomerEdit";
import InvalidFieldsForConfigurationBanner from "./InvalidFieldsForConfigurationBanner";
import CompanyDetails from "./CompanyDetails";
import CustomeList from "./company/CustomeList";
import CompanyDemographics from "./company/CompanyDemographics";
import CompanySocialMedia from "./company/SocialMedia";
import InterestedProjects from "./interested-project/InterestedProject";
import RequirementList from "./requirements/RequirementList";
import { EditEventPopup } from "./activities/edit-event-popup";
import { combineDateAndTime } from "../../../utils/CombineDateAndTime";

const Details: React.FunctionComponent<{
  onClose?: () => void;
  onReload?: () => void;
}> = (props) => {
  const appSettings = useAppSettings(api.settings);
  const masterData = useMasterData(api.settings);
  const canAssignCRMAgent = useHasPermission(AppPermissions.AssignCRMAgent);
  const canUpdateCrmAgent = useHasPermission(AppPermissions.CanUpdateCrmAgent);
  const canAssignSalesPerson = useHasPermission(
    AppPermissions.AssignSalesPerson
  );
  const canUpdateSalesPerson = useHasPermission(
    AppPermissions.CanUpdateSalesPerson
  );
  const canViewCustomerDetails = useHasPermission(
    AppPermissions.ViewCustomerDetails
  );

  const canViewLeadActivities = useHasPermission(
    AppPermissions.ViewLeadActivities
  );
  const canSetLeadQuality = useHasPermission(AppPermissions.SetLeadQuality);
  const canMapTags = useHasPermission(AppPermissions.MapTags);

  const canScheduleTestDrive = useHasPermission(
    AppPermissions.ScheduleTestDrive
  );
  const canRequestCallBack = useHasPermission(AppPermissions.RequestCallBack);
  const canAddVehicleToLead = useHasPermission(AppPermissions.AddVehicleToLead);
  const canScheduleShowroomVisit = useHasPermission(
    AppPermissions.ScheduleShowroomVisit
  );
  const canConvertToEnquiry = useHasPermission(AppPermissions.ConvertToEnquiry);
  const canCreateLeadActivity = useHasPermission(
    AppPermissions.CreateLeadActivity
  );

  const ViewRequirement = useHasPermission(
    AppPermissions.ViewRequirement
  );

  const [customerModal, setCustomerModal] = useState<{
    isOpen?: boolean;
    id?: string;
    entity?: string;
  }>({});

  const topScrollRef = React.useRef<HTMLDivElement>(null);

  const { updateCustomerDetails, updateCompanyDetails } = useLead(api.lead);
  const {
    showCalendar,
    selectedDate,
    setSelectedDate,
    selectedAssignee,
    setInitialData,
    relaodTrigger,
  } = useLeadCalendarContext();
  const { loadAllLog } = useLog(api.log);
  const leadState = useSelector((state: State) => state.lead);

  const [stage, setStage] = useState<string>("");
  const [closeAllAccordion, setCloseAllAccordion] = useState<boolean>(false);

  const dataToPass = leadState?.leadDetails?.company;
  const [resetState, setResetState] = useState(dataToPass);
  const [createNewActivity, setCreateNewActivity] = useState<any>();
  const authState = useSelector((state: State) => state.authUser);

  const startDateTime = combineDateAndTime(
    moment().startOf("day").toDate(),
    appSettings?.data?.application?.appointmentTime?.start || "9:30 AM"
  );

  const minimumDurationInMinutes = getMinutes(
    appSettings?.data?.activity?.activityMinimumDuration || {
      value: 15,
      unit: "Minutes",
    }
  );
  // const refreshData = React.useRef(resetState);
  const loadLead = async (leadId: string) => {
    const response = await api.lead.getLead(leadId);
    setResetState(response?.data?.company);
  };

  const updateUser = (data: Customer) => {
    updateCustomerDetails(data);
  };

  const updateCompany = (data: Company) => {
    if (leadState.leadDetails) {
      updateCompanyDetails(data);
    }
  };
  const onCancelAndStateRefresh = () => {
    if (leadState?.leadDetails?._id) {
      loadLead(leadState?.leadDetails?._id);
    }
  };
  const closeAccordion = () => {
    setCloseAllAccordion(true);
    topScrollRef?.current?.scrollIntoView();
  };

  const calculateDateDifference = (d1: Date, d2: Date) => {
    const date1 = moment(d1).startOf("day");
    const date2 = moment(d2).startOf("day");
    return date2.diff(date1, "days");
  };

  const canShowActions =
    (!leadState.leadDetails?.isDeal && canSetLeadQuality) ||
    canMapTags ||
    canScheduleTestDrive ||
    canRequestCallBack ||
    canAddVehicleToLead ||
    canScheduleShowroomVisit ||
    (!leadState.leadDetails?.isDeal && canConvertToEnquiry) ||
    canCreateLeadActivity;

  const invalidCustomerFieldsForEnquiry =
    !leadState?.leadDetails?.isDeal && leadState.leadDetails?.customer
      ? getInvalidCustomerFieldsEnquiry(
          leadState.leadDetails?.customer,
          masterData?.data?.leadStages
            ?.find((x) => x.code === "enquiry")
            ?.entity?.find((c) => c.key === "Customer")!
        )
      : [];

  const invalidCustomerFieldsForConfiguartion =
    leadState?.leadDetails?.isDeal && leadState.leadDetails?.customer
      ? getInvalidFields(
          leadState.leadDetails?.customer,
          masterData?.data?.leadStages?.find((x) => x.code === "configuration")
            ?.entity!,
          leadState?.leadDetails
        )
      : [];

      const addActivity =() => {
        setCreateNewActivity(true);
      }

  return (
    <>
      {leadState.leadDetails?.isDeal && (
        <div className="d-flex dealSections border-bottom font-xs font-weight-semibold text-gray-400 text-center">
          {leadState?.leadDetails?.stagesTransitions
            ?.sort(
              (a: StageTransition, b: StageTransition) =>
                a.position - b.position
            )
            ?.map((stages) => {
              return (
                <div style={{minHeight: '30px'}} title={`${capitalizeFirstLetter(stages.name)} ${stages.startedOn &&
                  calculateDateDifference(
                    new Date(stages.startedOn),
                    new Date()
                  ) > 0
                    ? `(${calculateDateDifference(
                        new Date(stages.startedOn),
                        new Date()
                      )} days)`
                    : ""}`}
                  className={`col py-1 d-flex align-items-center justify-content-center px-1 dealSections__item ${
                    stages.startedOn ? "text-gray-900" : ""
                  } `}
                >
                  <span className="text-truncate pl-3">{capitalizeFirstLetter(stages.name)}</span>
                 {" "}
                  {stages.startedOn &&
                  calculateDateDifference(
                    new Date(stages.startedOn),
                    new Date()
                  ) > 0
                    ? `(${calculateDateDifference(
                        new Date(stages.startedOn),
                        new Date()
                      )}D)`
                    : ""}
                    
                </div>
              );
            })}
        </div>
      )}
      {!leadState.leadDetails?.isDeal &&
        (leadState.leadDetails?.quality === LeadQuality.Interested ||
          leadState.leadDetails?.quality === LeadQuality.NotIntrested) && (
          <InvalidFieldsForEnquiryBanner
            invalidITems={invalidCustomerFieldsForEnquiry}
            onAction={() =>
              setCustomerModal({
                isOpen: true,
                id: leadState?.leadDetails?.customer?._id,
                entity: "enquiry",
              })
            }
          ></InvalidFieldsForEnquiryBanner>
        )}

      <div className="container-fluid px-0 h-100">
        <div className="row no-gutters h-100">
          <div className="col-md-4 col-12 border-right">
            {(stage === "fetch-customer" ||
              stage === "otp" ||
              stage === "create-customer") && (
              <div className="d-flex border-bottom align-items-center py-3 px-4">
                <h6 className="mb-0">Contact Details</h6>
              </div>
            )}
            {leadState?.leadDetails?.company ? (
              <>
                <CompanyDetails
                  details={resetState}
                  setStage={(stageName: string) => setStage(stageName)}
                  updateCompany={(details: Company) => updateCompany(details)}
                  // show360Link
                  onStateChange={() => onCancelAndStateRefresh()}
                ></CompanyDetails>
                {leadState?.leadDetails && (
                  <CustomeList
                    leadId={leadState?.leadDetails?._id}
                    companyId={leadState?.leadDetails?.company?._id}
                    updateUser={(details: Customer) =>
                      updateCustomerDetails(details)
                    }
                  ></CustomeList>
                )}
              </>
            ) : (
              <CustomerDetails
                customerDetails={leadState?.leadDetails?.customer}
                setStage={(stageName: string) => setStage(stageName)}
                updateUser={(details: Customer) => {
                  updateUser(details);
                  loadAllLog({
                    page: 1,
                    leadId: leadState?.leadDetails?._id,
                    customerId: leadState?.leadDetails?.customer?._id,
                    includeAllCustomerLogs: true,
                  });
                }}
                show360Link
              ></CustomerDetails>
            )}

            {leadState?.leadDetails && (
              <InterestedProjects
                onUpdate={() => {
                  loadAllLog({
                    page: 1,
                    leadId: leadState?.leadDetails?._id,
                    customerId: leadState?.leadDetails?.customer?._id,
                    includeAllCustomerLogs: true,
                  });
                }}
                onUpdateConfiguration={() => {
                  props.onReload?.();
                }}
              ></InterestedProjects>
            )}

            { ViewRequirement && !!leadState?.leadDetails?.requirements?.length && (
              <RequirementList
                onReload={() => {
                  props.onReload?.();
                }}
              ></RequirementList>
            )}

            <LinkedLeads id={leadState?.leadDetails?._id || ""}></LinkedLeads>
          </div>

          <>
            <div
              className="col-md-4 col-12 border-right accordion"
              ref={topScrollRef}
            >
              <Accordion
                defaultId="Assigned User"
                onAccordionClick={() => setCloseAllAccordion(false)}
                closeAll={closeAllAccordion}
              >
                {canShowActions &&
                  leadState?.leadDetails?.isEditable &&
                  !leadState.leadDetails?.customer?.isBlocked && (
                    <div id="Activities">
                      <Actions
                        lead={leadState}
                        onUpdate={() => {
                          loadAllLog({
                            page: 1,
                            leadId: leadState?.leadDetails?._id,
                            customerId: leadState?.leadDetails?.customer?._id,
                            includeAllCustomerLogs: true,
                          });
                        }}
                        disableConvertEnquiry={
                          !!invalidCustomerFieldsForEnquiry?.length
                        }
                        onReload={() => {
                          props.onReload?.();
                        }}
                      ></Actions>
                    </div>
                  )}

                {leadState?.leadDetails && (
                  <div id="Lead Settings">
                    <LeadEdit
                      onUpdate={() => {
                        loadAllLog({
                          page: 1,
                          leadId: leadState?.leadDetails?._id,
                          customerId: leadState?.leadDetails?.customer?._id,
                          includeAllCustomerLogs: true,
                        });
                        closeAccordion();
                      }}
                      leadDetails={leadState?.leadDetails}
                    ></LeadEdit>
                  </div>
                )}

                {((canAssignSalesPerson &&
                  !leadState.leadDetails?.currentSalesPerson) ||
                  (canUpdateSalesPerson &&
                    !!leadState.leadDetails?.currentSalesPerson) ||
                  (canAssignCRMAgent &&
                    !leadState.leadDetails?.currentCRMAgent) ||
                  (canUpdateCrmAgent &&
                    !!leadState.leadDetails?.currentCRMAgent)) &&
                  leadState?.leadDetails?.isEditable && (
                    <div id="Assigned User">
                      <AssignUsers
                        onUpdate={() => {
                          loadAllLog({
                            page: 1,
                            leadId: leadState?.leadDetails?._id,
                            customerId: leadState?.leadDetails?.customer?._id,
                            includeAllCustomerLogs: true,
                          });
                        }}
                      ></AssignUsers>
                    </div>
                  )}
                {/* {leadState?.leadDetails  && !leadState?.leadDetails?.company?._id && canViewCustomerDetails && (
                  <div id="Demographics">
                    {!leadState?.leadDetails?.company?._id && (
                      <Demographics
                        customerDetails={leadState?.leadDetails?.customer}
                        updated={() => {
                          closeAccordion();
                          loadAllLog({
                            page: 1,
                            leadId: leadState?.leadDetails?._id,
                            customerId: leadState?.leadDetails?.customer?._id,
                            includeAllCustomerLogs: true,
                          });
                        }}
                      ></Demographics>
                    )}
                    {!!leadState?.leadDetails?.company?._id && (
                      <CompanyDemographics></CompanyDemographics>
                    )}
                  </div>
                )} */}
                
                {/* {canViewCustomerDetails && !leadState?.leadDetails?.company && (
                  <div id="Work Experience">
                    <WorkExperience
                      updated={() => {
                        closeAccordion();
                        loadAllLog({
                          page: 1,
                          leadId: leadState?.leadDetails?._id,
                          customerId: leadState?.leadDetails?.customer?._id,
                          includeAllCustomerLogs: true,
                        });
                      }}
                    ></WorkExperience>
                  </div>
                )} */}
                {/* {canViewCustomerDetails && !leadState?.leadDetails?.company && (
                  <div id="Social Media">
                    <SocialMedia
                      updated={() => {
                        closeAccordion();
                        loadAllLog({
                          page: 1,
                          leadId: leadState?.leadDetails?._id,
                          customerId: leadState?.leadDetails?.customer?._id,
                          includeAllCustomerLogs: true,
                        });
                      }}
                    ></SocialMedia>
                  </div>
                )} */}
                {/* {canViewCustomerDetails && !!leadState?.leadDetails?.company && (
                  <div id="Social Media">
                    <CompanySocialMedia
                      updated={() => {
                        closeAccordion();
                        loadAllLog({
                          page: 1,
                          leadId: leadState?.leadDetails?._id,
                          customerId: leadState?.leadDetails?.customer?._id,
                          includeAllCustomerLogs: true,
                        });
                      }}
                    ></CompanySocialMedia>
                  </div>
                )} */}
                {/* {canViewCustomerDetails && (
                  <div id="Preferred Time For Conversation">
                    <PreferredTime
                      updated={() => {
                        closeAccordion();
                        loadAllLog({
                          page: 1,
                          leadId: leadState?.leadDetails?._id,
                          customerId: leadState?.leadDetails?.customer?._id,
                          includeAllCustomerLogs: true,
                        });
                      }}
                    ></PreferredTime>
                  </div>
                )}
                {canViewCustomerDetails && (
                  <div id="Vehicle User Details">
                    <VehicleUserDetails
                      onUpdate={() => {
                        loadAllLog({
                          page: 1,
                          leadId: leadState?.leadDetails?._id,
                          customerId: leadState?.leadDetails?.customer?._id,
                          includeAllCustomerLogs: true,
                        });
                      }}
                    ></VehicleUserDetails>
                  </div>
                )} */}
                {/* {leadState?.leadDetails && canViewCustomerDetails && (
                  <div id="Need Profiling">
                    <NeedProfiling
                      updated={() => {
                        closeAccordion();
                        loadAllLog({
                          page: 1,
                          leadId: leadState?.leadDetails?._id,
                          customerId: leadState?.leadDetails?.customer?._id,
                          includeAllCustomerLogs: true,
                        });
                      }}
                    ></NeedProfiling>
                  </div>
                )} */}
                {/* {leadState?.leadDetails && canViewCustomerDetails && (
                  <div id="Business Partner Relationship">
                    <BusinessPartnerRelationship
                      updated={() => {
                        closeAccordion();
                        loadAllLog({
                          page: 1,
                          leadId: leadState?.leadDetails?._id,
                          customerId: leadState?.leadDetails?.customer?._id,
                          includeAllCustomerLogs: true,
                        });
                      }}
                    ></BusinessPartnerRelationship>
                  </div>
                )}
                {canViewCustomerDetails && (
                  <div id="Buying Influences">
                    <BuyingInfluences
                      onUpdate={() => {
                        loadAllLog({
                          page: 1,
                          leadId: leadState?.leadDetails?._id,
                          customerId: leadState?.leadDetails?.customer?._id,
                          includeAllCustomerLogs: true,
                        });
                      }}
                    ></BuyingInfluences>
                  </div>
                )} */}
                {/* {leadState?.leadDetails &&
                  canViewCustomerDetails &&
                  !leadState?.leadDetails?.company && (
                    <div id="Documents">
                      <Documents
                        customerDetails={leadState?.leadDetails?.customer}
                        updated={() => {
                          loadAllLog({
                            page: 1,
                            leadId: leadState?.leadDetails?._id,
                            customerId: leadState?.leadDetails?.customer?._id,
                            includeAllCustomerLogs: true,
                          });
                        }}
                      ></Documents>
                    </div>
                  )} */}
                
              </Accordion>
            </div>
            {canViewLeadActivities ? (
              <>
                {!showCalendar && (
                  <LeadActivities
                    id={leadState?.leadDetails?._id!}
                    onUpdate={() => {
                      loadAllLog({
                        page: 1,
                        leadId: leadState?.leadDetails?._id,
                        customerId: leadState?.leadDetails?.customer?._id,
                        includeAllCustomerLogs: true,
                      });
                      closeAccordion();
                    }}
                    onReload={props.onReload}
                    addActivity={addActivity}
                  ></LeadActivities>
                )}
                 {createNewActivity &&
                  <EditEventPopup
                    action={{
                      "name": "Create Activity",
                      "code": "call",
                      "_id": "call"
                    }}
                    isEdit={false}
                    currentActivity={{
                      "type": "call",
                      "title": "",
                      timePeriod: {
                        startDate: new Date(),
                        startTime:
                          appSettings?.data?.application?.appointmentTime?.start ||
                          "9:00 AM",
                        endTime:
                          moment(startDateTime)
                            .add(minimumDurationInMinutes, "minute")
                            .format(DateFormats.TimeOnlyFormat) || "9:30 AM",
                      },
                      salesPerson: {
                        _id: authState.profile?._id || "",
                        name: authState.profile?.name,
                        knownAs: authState.profile?.knownAs,
                      },
                      company : {
                        "_id": leadState?.leadDetails?.company?._id 
                      },
                      "busyEvent": true,
                    }}
                    onClose={() => {
                      setCreateNewActivity(undefined);
                    }}
                    onSubmit={(updatedEvent) => {
                      // props.onReload?.();
                      // setCreateNewActivity(undefined); 
                    }}
                    leadId={leadState?.leadDetails?._id}
                    onSubmitCompleted={() => {
                      props.onReload?.();
                      setCreateNewActivity(undefined); 
                    }}
                  ></EditEventPopup>}
                {showCalendar && (
                  <div className="col-md-4 col-12">
                    <div className="position-sticky top-detail-head-height">
                      <EditEventCalendarWraper
                        resourceId={selectedAssignee}
                        selectedDate={selectedDate!}
                        onDateChange={(date: Date) => {
                          setSelectedDate(date);
                        }}
                        reloadTrigger={relaodTrigger}
                        onSelectSlot={(start: Date, end: Date) => {
                          const activityModel: ActivityModel = {
                            timePeriod: {
                              startDate: moment(start).endOf("day").toDate(),
                              startTime: formatDate(
                                start,
                                DateFormats.TimeOnlyFormat
                              ),
                              endTime: formatDate(
                                end,
                                DateFormats.TimeOnlyFormat
                              ),
                            },
                          };
                          setInitialData(activityModel);
                        }}
                      ></EditEventCalendarWraper>
                    </div>
                  </div>
                )}
                {customerModal?.isOpen && (
                  <>
                    <CustomerEdit
                      onClose={() => setCustomerModal({})}
                      id={customerModal?.id || ""}
                      onReload={() => {
                        props.onReload?.();
                        setCustomerModal({});
                      }}
                      entity={
                        masterData?.data?.leadStages?.find(
                          (x) => x.code === customerModal.entity
                        )?.entity!
                      }
                      leadDetails={leadState?.leadDetails}
                    />
                  </>
                )}
              </>
            ) : (
              <>
                <div className="col-md col-12"></div>
              </>
            )}
          </>
        </div>
      </div>
    </>
  );
};

export default Details;
